import { useContext, useEffect, useRef, useState } from "react"
import { Box } from '@mui/material'
import Lottie from 'lottie-react'
import { useLinkageOrganizationAPI } from '../../LinkageCreationContext'
import GlobalContext from "../../../../../../../../context/global-context"
import LoaderMain from '../../../../../../../../assets/svgIcons/loaderMain.json'
import { Title } from "../../../../../../ui"
import CustomButton from "../../../../../../ui/CustomButton"
import ResizableModal from "../../../../../../ui/ResizableModal"
import ListCaja from "./ListCaja"
import { logMassiveLinkageDebug } from './massiveLinkageDebug'

const InputField = ({ label, placeholder, type, value, onChange, disabled = false }) => (
  <div>
    <label className='block text-sm font-medium text-gray-700'>{label}</label>
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`mt-1 block w-full rounded-full border-gray-300 shadow-sm sm:text-sm
        ${disabled
          ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
          : 'focus:border-indigo-500 focus:ring-indigo-500'}
      `}
    />
  </div>
)

const getRowAmount = row => Number(row?.montoSeleccionable ?? row?.montoTotal ?? 0)
const areIdListsEqual = (left = [], right = []) =>
  left.length === right.length && left.every((value, index) => value === right[index])

const CobroCajaPrincipal = ({
  open,
  setOpen,
  type = 'ingreso',
  setSwitchDate,
  idLibro,
  fVenc,
  folio,
  onMassivePeriodTrace
}) => {
  const [fecha, setFecha] = useState("")
  const [montoTotalSeleccionado, setMontoTotalSeleccionado] = useState(0)
  const [filasSeleccionadas, setFilasSeleccionadas] = useState([])
  const [rowsSeleccionables, setRowsSeleccionables] = useState([])
  const [filterContext, setFilterContext] = useState({
    monthFilter: 0,
    yearFilter: null,
    monthLabel: 'todos'
  })

  const organizationAPI = useLinkageOrganizationAPI()
  const { currentBusiness } = useContext(GlobalContext)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const submitLockRef = useRef(false)
  const handleClose = () => setOpen(false)
  const hasSpecificMonthSelected = Number(filterContext.monthFilter) > 0

  const handleSeleccionar = (row, allRows = []) => {
    if (row === 'SELECCIONAR_TODOS') {
      const nuevasIds = allRows.map(r => r.id)
      setFilasSeleccionadas(previousIds => [...new Set([...previousIds, ...nuevasIds])])
      return
    }

    if (row === 'DESELECCIONAR_TODOS') {
      const visibleIds = new Set(allRows.map(r => r.id))
      setFilasSeleccionadas(previousIds => previousIds.filter(id => !visibleIds.has(id)))
      return
    }

    const yaSeleccionado = filasSeleccionadas.includes(row.id)
    if (yaSeleccionado) {
      setFilasSeleccionadas(filasSeleccionadas.filter(id => id !== row.id))
      setMontoTotalSeleccionado(prev => prev - getRowAmount(row))
    } else {
      setFilasSeleccionadas([...filasSeleccionadas, row.id])
      setMontoTotalSeleccionado(prev => prev + getRowAmount(row))
    }
  }

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0]
    setFecha(today)
  }, [])

  useEffect(() => {
    const selectedRows = rowsSeleccionables.filter(row => filasSeleccionadas.includes(row.id))
    const nextSelectedIds = selectedRows.map(row => row.id)
    const nextTotal = selectedRows.reduce((sum, row) => sum + getRowAmount(row), 0)

    if (!areIdListsEqual(filasSeleccionadas, nextSelectedIds)) {
      setFilasSeleccionadas(nextSelectedIds)
    }

    if (montoTotalSeleccionado !== nextTotal) {
      setMontoTotalSeleccionado(nextTotal)
    }
  }, [filasSeleccionadas, montoTotalSeleccionado, rowsSeleccionables])

  const handleCaja = async () => {
    if (submitLockRef.current || !hasSpecificMonthSelected) return

    submitLockRef.current = true
    setIsSubmitting(true)

    try {
      const selectedRows = rowsSeleccionables
        .filter(row => filasSeleccionadas.includes(row.id))
      const ids = selectedRows.map(row => row.idPrincipal)

      const body = { ids }

      const debugRows = logMassiveLinkageDebug({
        scope: 'CobroCajaPrincipal',
        body,
        filterContext,
        selectedRows
      })

      const detectedPeriods = [...new Set(
        debugRows
          .map(item => item.periodoOrigenDetectado)
          .filter(Boolean)
      )]

      await organizationAPI.postCajaMultiple(
        { businessId: currentBusiness.id },
        body
      )

      const tracePayload = {
        source: 'cobro-masivo',
        filterContext,
        detectedPeriods,
        movementDate: fecha,
        movementAmount: montoTotalSeleccionado,
        selectedCount: ids.length,
        selectedIds: ids,
        documents: debugRows
      }

      onMassivePeriodTrace?.(tracePayload)

      setSwitchDate(true)
      handleClose()
    } catch (error) {
      console.error(error)
      submitLockRef.current = false
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    if (!open) {
      submitLockRef.current = false
      setIsSubmitting(false)
      setFilasSeleccionadas([])
      setMontoTotalSeleccionado(0)
    }
  }, [open])

  return (
    <ResizableModal
      open={open}
      onClose={handleClose}
      footer={
        <>
          <CustomButton
            title='CANCELAR'
            color='primary'
            className='rounded-full'
            onClick={handleClose}
            disabled={isSubmitting}
          />
          <CustomButton
            title={isSubmitting ? 'GRABANDO...' : 'GRABAR VINCULACION'}
            color='accent2'
            className='rounded-full'
            onClick={handleCaja}
            disabled={isSubmitting || !hasSpecificMonthSelected}
          />
        </>
      }
    >
      <>
        {isSubmitting ? (
          <Box
            display='flex'
            flexDirection='column'
            justifyContent='center'
            alignItems='center'
            height='100%'
            gap={2}
          >
            <Lottie
              animationData={LoaderMain}
              loop
              autoplay
              style={{ width: 100, height: 100 }}
            />
            <div className='max-w-md px-6 text-center font-sans'>
              <p className='text-sm font-semibold text-slate-700'>
                Estamos procesando el cobro masivo.
              </p>
              <p className='mt-1 text-xs leading-5 text-slate-500'>
                Este proceso puede tardar unos minutos. No cierres esta pestaña ni recargues la
                página hasta que finalice.
              </p>
            </div>
          </Box>
        ) : (
          <div className="mt-4">
            <div className='flex justify-between items-center mb-6'>
              <div>
                <Title className='text-3xl'>Cobro Masivo de Documentos</Title>
              </div>
            </div>
            <div
              className="p-4 mt-2 bg-white rounded-3xl shadow-md flex flex-col overflow-hidden"
              style={{ height: 'calc(100vh - 12rem)' }}
            >
              <div className="flex w-full items-start">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4 w-full">
                  <InputField label="Folio" value={folio} type="text" disabled />
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Fecha</label>
                    <input
                      type="date"
                      value={fecha}
                      onChange={(e) => setFecha(e.target.value)}
                      className="mt-1 block w-full rounded-full border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                  <InputField
                    label="Monto Total"
                    type="text"
                    value={montoTotalSeleccionado.toLocaleString('es-CL')}
                    readOnly
                  />
                </div>
              </div>
              <div className="mt-6 px-6 overflow-auto">
                <ListCaja
                  type={type}
                  onSeleccionar={handleSeleccionar}
                  filasSeleccionadas={filasSeleccionadas}
                  setRowsSeleccionables={setRowsSeleccionables}
                  onFilterContextChange={setFilterContext}
                />
                {!hasSpecificMonthSelected && (
                  <p className='mt-4 px-1 text-sm font-medium text-amber-700'>
                    La vinculación masiva se habilita solo cuando eliges un mes específico.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    </ResizableModal>
  )
}

export default CobroCajaPrincipal
